import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { billingService } from "@/api/services";
import {
  getErrorMessage,
  isErrorType,
  formatErrorMessage,
} from "@/utils/errorUtils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Search,
  FileText,
  DollarSign,
  Clock,
  CheckCircle,
  AlertTriangle,
  ExternalLink,
  RefreshCw,
  Check,
  Ban,
  Link as LinkIcon,
  Plus,
  Edit,
  Trash2,
} from "lucide-react";
import { showSuccess, showError, showWarning } from "@/utils/toastHelpers";
import InvoiceForm from "@/components/billing/InvoiceForm";

export default function Billing() {
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [showInvoiceForm, setShowInvoiceForm] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [sortOrder, setSortOrder] = useState("none");

  // Fetch Xero connection status
  const {
    data: xeroStatus,
    isLoading: isLoadingXeroStatus,
    error: xeroStatusError,
  } = useQuery({
    queryKey: ["xeroStatus"],
    queryFn: billingService.getXeroStatus,
  });

  // Fetch ALL invoices for stats (unfiltered)
  const { data: allInvoices = [] } = useQuery({
    queryKey: ["invoices", "all"],
    queryFn: () => billingService.getInvoices({}),
  });

  // Fetch filtered invoices for display
  const { data: invoices = [], isLoading } = useQuery({
    queryKey: ["invoices", statusFilter],
    queryFn: () =>
      billingService.getInvoices(
        statusFilter !== "all" ? { status: statusFilter } : {}
      ),
  });

  // Connect to Xero mutation
  const connectXero = useMutation({
    mutationFn: billingService.connectXero,
    onSuccess: (data) => {
      if (data?.authUrl) {
        window.location.href = data.authUrl;
      }
    },
    onError: (error) => {
      console.error("Failed to connect to Xero:", error);
      showError("Failed to connect to Xero", getErrorMessage(error));
    },
  });

  // Disconnect Xero mutation
  const disconnectXero = useMutation({
    mutationFn: billingService.disconnectXero,
    onSuccess: () => {
      queryClient.invalidateQueries(["xeroStatus"]);
      showSuccess("Disconnected from Xero successfully");
    },
    onError: (error) => {
      console.error("Failed to disconnect from Xero:", error);
      showError("Failed to disconnect from Xero", getErrorMessage(error));
    },
  });

  // Finalize invoice mutation
  const finalizeInvoice = useMutation({
    mutationFn: (id) => billingService.finalizeInvoice(id),
    onSuccess: () => {
      queryClient.invalidateQueries(["invoices"]);
      showSuccess("Invoice finalized successfully");
      setSelectedInvoice(null);
    },
    onError: (error) => {
      console.error("Failed to finalize invoice:", error);
      showError("Failed to finalize invoice", getErrorMessage(error));
    },
  });

  // Sync invoice mutation
  const syncInvoice = useMutation({
    mutationFn: (id) => billingService.syncInvoice(id),
    onSuccess: async (data, invoiceId) => {
      // Invalidate and refetch invoices list
      await queryClient.invalidateQueries(["invoices"]);

      // Fetch the updated invoice and update the modal
      try {
        const updatedInvoice = await billingService.getInvoice(invoiceId);
        setSelectedInvoice(updatedInvoice);
      } catch (error) {
        console.error("Failed to fetch updated invoice:", error);
      }

      showSuccess("Invoice synced successfully");
    },
    onError: (error) => {
      console.error("Failed to sync invoice:", error);

      // Check if it's a Xero connection error
      if (
        isErrorType(error, [
          "refresh",
          "token",
          "Xero not connected",
          "Xero connection expired",
        ])
      ) {
        showError(
          "Xero connection expired",
          "Your Xero connection appears to have expired. Please:\n1. Disconnect from Xero\n2. Reconnect to Xero\n3. Try syncing again"
        );
      } else {
        showError("Failed to sync invoice", getErrorMessage(error));
      }
    },
  });

  // Delete invoice mutation
  const deleteInvoice = useMutation({
    mutationFn: (id) => billingService.deleteInvoice(id),
    onSuccess: () => {
      queryClient.invalidateQueries(["invoices"]);
      showSuccess("Invoice deleted successfully");
      setSelectedInvoice(null);
    },
    onError: (error) => {
      console.error("Failed to delete invoice:", error);
      showError("Failed to delete invoice", getErrorMessage(error));
    },
  });

  // Create invoice mutation
  const createInvoice = useMutation({
    mutationFn: (invoiceData) => billingService.createInvoice(invoiceData),
    onSuccess: () => {
      queryClient.invalidateQueries(["invoices"]);
      showSuccess("Invoice created successfully");
      setShowInvoiceForm(false);
    },
    onError: (error) => {
      console.error("Failed to create invoice:", error);
      showError("Failed to create invoice", getErrorMessage(error));
    },
  });

  // Update invoice mutation
  const updateInvoice = useMutation({
    mutationFn: ({ id, data }) => billingService.updateInvoice(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(["invoices"]);
      showSuccess("Invoice updated successfully");
      setEditingInvoice(null);
    },
    onError: (error) => {
      console.error("Failed to update invoice:", error);
      showError("Failed to update invoice", getErrorMessage(error));
    },
  });

  // Calculate statistics from ALL invoices (not filtered)
  const stats = {
    draft: allInvoices.filter((i) => i.status === "draft").length,
    authorised: allInvoices.filter((i) => i.status === "authorised").length,
    paid: allInvoices.filter((i) => i.status === "paid").length,
    total: allInvoices.reduce((sum, i) => sum + (i.total || 0), 0),
  };

  // Filter and sort invoices
  const filteredInvoices = invoices
    .filter((invoice) => {
      const searchMatch =
        invoice.patientName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        invoice.patientEmail?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        invoice.xeroInvoiceNumber
          ?.toLowerCase()
          .includes(searchTerm.toLowerCase());
      return searchMatch;
    })
    .sort((a, b) => {
      if (sortOrder === "none") return 0;
      const dateA = a.dueDate ? new Date(a.dueDate).getTime() : 0;
      const dateB = b.dueDate ? new Date(b.dueDate).getTime() : 0;
      return sortOrder === "asc" ? dateA - dateB : dateB - dateA;
    });

  const getStatusBadge = (status) => {
    const badges = {
      draft: (
        <span className="px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 flex items-center gap-1">
          <Clock className="w-3 h-3" />
          Draft
        </span>
      ),
      authorised: (
        <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 flex items-center gap-1">
          <FileText className="w-3 h-3" />
          Awaiting Payment
        </span>
      ),
      paid: (
        <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 flex items-center gap-1">
          <CheckCircle className="w-3 h-3" />
          Paid
        </span>
      ),
      voided: (
        <span className="px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800 flex items-center gap-1">
          <Ban className="w-3 h-3" />
          Voided
        </span>
      ),
    };
    return badges[status] || badges.draft;
  };

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 mb-2">Billing</h1>
          <p className="text-slate-600">Manage invoices and Xero integration</p>
        </div>

        <div className="flex flex-col items-end gap-2">
          <Button
            onClick={() => setShowInvoiceForm(true)}
            disabled={!xeroStatus?.connected}
            className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Plus className="w-4 h-4 mr-2" />
            New Service Invoice
          </Button>
          {!xeroStatus?.connected && (
            <p className="text-xs text-amber-600">
              Connect to Xero to create invoices
            </p>
          )}
        </div>
      </div>

      {/* Xero Connection Section */}
      <div className="bg-white rounded-lg shadow border border-slate-200 p-6">
        <h2 className="text-lg font-semibold text-slate-900 mb-4">
          Xero Integration
        </h2>
        <div className="flex items-center gap-3">
          {isLoadingXeroStatus ? (
            <div className="flex items-center gap-2 px-4 py-2 bg-slate-50 border border-slate-200 rounded-lg">
              <RefreshCw className="w-4 h-4 text-slate-600 animate-spin" />
              <div className="text-sm">
                <div className="font-medium text-slate-700">
                  Checking connection...
                </div>
              </div>
            </div>
          ) : xeroStatusError ? (
            <div className="flex items-center gap-2 px-4 py-2 bg-red-50 border border-red-200 rounded-lg">
              <AlertTriangle className="w-4 h-4 text-red-600" />
              <div className="text-sm">
                <div className="font-medium text-red-900">Connection Error</div>
                <div className="text-red-600">
                  {getErrorMessage(xeroStatusError)}
                </div>
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={() => connectXero.mutate()}
                disabled={connectXero.isPending}
                className="ml-2"
              >
                Retry Connection
              </Button>
            </div>
          ) : xeroStatus?.connected ? (
            <div className="flex items-center gap-2 px-4 py-2 bg-green-50 border border-green-200 rounded-lg">
              <LinkIcon className="w-4 h-4 text-green-600" />
              <div className="text-sm">
                <div className="font-medium text-green-900">
                  Connected to Xero
                </div>
                <div className="text-green-600">
                  {xeroStatus.tenantName || "Unknown Organization"}
                </div>
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={() => disconnectXero.mutate()}
                disabled={disconnectXero.isPending}
              >
                Disconnect
              </Button>
            </div>
          ) : (
            <Button
              onClick={() => connectXero.mutate()}
              disabled={connectXero.isPending}
              className="bg-blue-600 hover:bg-blue-700"
            >
              <LinkIcon className="w-4 h-4 mr-2" />
              Connect to Xero
            </Button>
          )}
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 p-6 rounded-lg border border-yellow-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-yellow-700">Draft</p>
              <p className="text-2xl font-bold text-yellow-900 mt-1">
                {stats.draft}
              </p>
            </div>
            <Clock className="w-8 h-8 text-yellow-600" />
          </div>
        </div>

        <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-6 rounded-lg border border-blue-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-blue-700">
                Awaiting Payment
              </p>
              <p className="text-2xl font-bold text-blue-900 mt-1">
                {stats.authorised}
              </p>
            </div>
            <FileText className="w-8 h-8 text-blue-600" />
          </div>
        </div>

        <div className="bg-gradient-to-br from-green-50 to-green-100 p-6 rounded-lg border border-green-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-green-700">Paid</p>
              <p className="text-2xl font-bold text-green-900 mt-1">
                {stats.paid}
              </p>
            </div>
            <CheckCircle className="w-8 h-8 text-green-600" />
          </div>
        </div>

        <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-6 rounded-lg border border-purple-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-purple-700">
                Total Revenue
              </p>
              <p className="text-2xl font-bold text-purple-900 mt-1">
                R {stats.total.toFixed(2)}
              </p>
            </div>
            <DollarSign className="w-8 h-8 text-purple-600" />
          </div>
        </div>
      </div>

      {/* Search and Filter */}
      <div className="flex flex-col md:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
          <Input
            placeholder="Search by patient name, email, or invoice number..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-3 py-2 border border-slate-200 rounded-md"
        >
          <option value="all">All Statuses</option>
          <option value="draft">Draft</option>
          <option value="authorised">Awaiting Payment</option>
          <option value="paid">Paid</option>
          <option value="voided">Voided</option>
        </select>
        <select
          value={sortOrder}
          onChange={(e) => setSortOrder(e.target.value)}
          className="px-3 py-2 border border-slate-200 rounded-md"
        >
          <option value="none">Sort by Due Date</option>
          <option value="asc">Due Date (Earliest First)</option>
          <option value="desc">Due Date (Latest First)</option>
        </select>
      </div>

      {/* Invoices List */}
      <div className="bg-white rounded-lg shadow border border-slate-200">
        <div className="px-6 py-4 border-b border-slate-200">
          <h2 className="text-lg font-semibold text-slate-900">Invoices</h2>
        </div>

        {isLoading ? (
          <div className="p-8 text-center text-slate-600">
            Loading invoices...
          </div>
        ) : filteredInvoices.length === 0 ? (
          <div className="p-8 text-center text-slate-600">
            <FileText className="w-12 h-12 text-slate-400 mx-auto mb-3" />
            <p>No invoices found</p>
          </div>
        ) : (
          <div className="divide-y divide-slate-200">
            {filteredInvoices.map((invoice) => (
              <div
                key={invoice.id}
                className="p-6 hover:bg-slate-50 transition-colors"
              >
                <div className="flex items-start justify-between">
                  <div
                    className="flex-1 cursor-pointer"
                    onClick={() => setSelectedInvoice(invoice)}
                  >
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="font-semibold text-slate-900">
                        {invoice.patientName || "Unknown Patient"}
                      </h3>
                      {getStatusBadge(invoice.status)}
                      {invoice.xeroInvoiceNumber && (
                        <span className="text-sm text-slate-500">
                          {invoice.xeroInvoiceNumber}
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-slate-600 mb-2">
                      {invoice.patientEmail}
                    </p>
                    <div className="flex items-center gap-4 text-xs text-slate-500">
                      <span>
                        Date:{" "}
                        {invoice.invoiceDate
                          ? new Date(invoice.invoiceDate).toLocaleDateString()
                          : "N/A"}
                      </span>
                      <span>
                        Due:{" "}
                        {invoice.dueDate
                          ? new Date(invoice.dueDate).toLocaleDateString()
                          : "N/A"}
                      </span>
                      {invoice.prescriptionId && (
                        <span>Prescription ID: {invoice.prescriptionId}</span>
                      )}
                    </div>
                  </div>

                  <div className="text-right flex flex-col items-end gap-2">
                    <div>
                      <p className="text-lg font-bold text-slate-900">
                        R {invoice.total?.toFixed(2) || "0.00"}
                      </p>
                      <p className="text-sm text-slate-500">
                        {invoice.lineItems?.length || 0} item(s)
                      </p>
                    </div>
                    {invoice.status === "draft" && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={(e) => {
                          e.stopPropagation();
                          setEditingInvoice(invoice);
                        }}
                      >
                        <Edit className="w-3 h-3 mr-1" />
                        Edit
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Invoice Detail Modal */}
      {selectedInvoice && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="px-6 py-4 border-b border-slate-200 flex justify-between items-center">
              <div>
                <h2 className="text-xl font-bold text-slate-900">
                  Invoice Details
                </h2>
                {selectedInvoice.xeroInvoiceNumber && (
                  <p className="text-sm text-slate-600">
                    {selectedInvoice.xeroInvoiceNumber}
                  </p>
                )}
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setSelectedInvoice(null)}
              >
                Close
              </Button>
            </div>

            {/* Content */}
            <div className="p-6 space-y-6">
              {/* Patient Info */}
              <div>
                <h3 className="text-sm font-medium text-slate-700 mb-2">
                  Patient Information
                </h3>
                <div className="bg-slate-50 p-4 rounded-lg">
                  <p className="font-medium text-slate-900">
                    {selectedInvoice.patientName || "Unknown Patient"}
                  </p>
                  <p className="text-sm text-slate-600">
                    {selectedInvoice.patientEmail}
                  </p>
                </div>
              </div>

              {/* Status and Dates */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <h3 className="text-sm font-medium text-slate-700 mb-2">
                    Status
                  </h3>
                  {getStatusBadge(selectedInvoice.status)}
                </div>
                <div>
                  <h3 className="text-sm font-medium text-slate-700 mb-2">
                    Due Date
                  </h3>
                  <p className="text-sm text-slate-900">
                    {selectedInvoice.dueDate
                      ? new Date(selectedInvoice.dueDate).toLocaleDateString()
                      : "N/A"}
                  </p>
                </div>
              </div>

              {/* Line Items */}
              <div>
                <h3 className="text-sm font-medium text-slate-700 mb-2">
                  Line Items
                </h3>
                <div className="border border-slate-200 rounded-lg overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-medium text-slate-600">
                          Description
                        </th>
                        <th className="px-4 py-2 text-right text-xs font-medium text-slate-600">
                          Qty
                        </th>
                        <th className="px-4 py-2 text-right text-xs font-medium text-slate-600">
                          Unit Price
                        </th>
                        <th className="px-4 py-2 text-right text-xs font-medium text-slate-600">
                          Total
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200">
                      {selectedInvoice.lineItems?.map((item, index) => (
                        <tr key={index}>
                          <td className="px-4 py-3 text-sm text-slate-900">
                            {item.description}
                          </td>
                          <td className="px-4 py-3 text-sm text-slate-600 text-right">
                            {item.quantity}
                          </td>
                          <td className="px-4 py-3 text-sm text-slate-600 text-right">
                            R {item.unitPrice?.toFixed(2) || "0.00"}
                          </td>
                          <td className="px-4 py-3 text-sm text-slate-900 text-right font-medium">
                            R {item.lineAmount?.toFixed(2) || "0.00"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className="bg-slate-50">
                      <tr>
                        <td
                          colSpan="3"
                          className="px-4 py-3 text-right text-sm font-medium text-slate-700"
                        >
                          Subtotal
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-900 text-right font-medium">
                          R {selectedInvoice.subtotal?.toFixed(2) || "0.00"}
                        </td>
                      </tr>
                      <tr>
                        <td
                          colSpan="3"
                          className="px-4 py-3 text-right text-sm font-medium text-slate-700"
                        >
                          Tax
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-900 text-right font-medium">
                          R {selectedInvoice.taxAmount?.toFixed(2) || "0.00"}
                        </td>
                      </tr>
                      <tr>
                        <td
                          colSpan="3"
                          className="px-4 py-3 text-right text-base font-bold text-slate-900"
                        >
                          Total
                        </td>
                        <td className="px-4 py-3 text-base text-slate-900 text-right font-bold">
                          R {selectedInvoice.total?.toFixed(2) || "0.00"}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-4 border-t border-slate-200">
                {selectedInvoice.status === "draft" && (
                  <Button
                    onClick={() => finalizeInvoice.mutate(selectedInvoice.id)}
                    disabled={finalizeInvoice.isPending}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    <Check className="w-4 h-4 mr-2" />
                    Finalize Invoice
                  </Button>
                )}

                {selectedInvoice.xeroInvoiceId && (
                  <Button
                    onClick={() => syncInvoice.mutate(selectedInvoice.id)}
                    disabled={syncInvoice.isPending}
                    variant="outline"
                  >
                    <RefreshCw
                      className={`w-4 h-4 mr-2 ${syncInvoice.isPending ? "animate-spin" : ""}`}
                    />
                    {syncInvoice.isPending ? "Syncing..." : "Sync from Xero"}
                  </Button>
                )}

                <Button
                  onClick={() => {
                    if (
                      window.confirm(
                        "Are you sure you want to delete this invoice? This action cannot be undone."
                      )
                    ) {
                      deleteInvoice.mutate(selectedInvoice.id);
                    }
                  }}
                  disabled={deleteInvoice.isPending}
                  variant="outline"
                  className="text-red-600 hover:text-red-700"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete Invoice
                </Button>

                {selectedInvoice.xeroInvoiceId && (
                  <Button
                    variant="outline"
                    onClick={() =>
                      window.open(
                        `https://go.xero.com/AccountsReceivable/View.aspx?InvoiceID=${selectedInvoice.xeroInvoiceId}`,
                        "_blank"
                      )
                    }
                  >
                    <ExternalLink className="w-4 h-4 mr-2" />
                    View in Xero
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Invoice Form for Creating New Invoice */}
      {showInvoiceForm && (
        <InvoiceForm
          invoice={null}
          onSave={(invoiceData) => createInvoice.mutate(invoiceData)}
          onClose={() => setShowInvoiceForm(false)}
          isLoading={createInvoice.isPending}
        />
      )}

      {/* Invoice Form for Editing Existing Invoice */}
      {editingInvoice && (
        <InvoiceForm
          invoice={editingInvoice}
          onSave={(invoiceData) =>
            updateInvoice.mutate({ id: editingInvoice.id, data: invoiceData })
          }
          onClose={() => setEditingInvoice(null)}
          isLoading={updateInvoice.isPending}
        />
      )}
    </div>
  );
}
